// src/services/api.js (cliente)
import axios from 'axios';
import router from '@/router';

const CLAVE_TOKEN_ACCESO = 'token_acceso_cliente';
const CLAVE_TOKEN_REFRESCO = 'token_refresco_cliente';
const CLAVE_USUARIO = 'usuario_cliente';
const CLAVE_MODULOS = 'modulos_cliente';

const CLAVE_TOKEN_ACCESO_LEGADO = 'client_access_token';
const CLAVE_TOKEN_REFRESCO_LEGADO = 'client_refresh_token';
const CLAVE_USUARIO_LEGADO = 'client_user';
const CLAVE_MODULOS_LEGADO = 'client_modules';

function obtenerTokenAcceso() {
  return localStorage.getItem(CLAVE_TOKEN_ACCESO) || localStorage.getItem(CLAVE_TOKEN_ACCESO_LEGADO);
}

function obtenerTokenRefresco() {
  return localStorage.getItem(CLAVE_TOKEN_REFRESCO) || localStorage.getItem(CLAVE_TOKEN_REFRESCO_LEGADO);
}

// Decodifica el payload de un JWT sin verificar firma (solo para leer exp)
function decodificarPayloadJwt(token) {
  try {
    const partes = token.split('.');
    if (partes.length !== 3) return null;
    const payload = JSON.parse(atob(partes[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload;
  } catch {
    return null;
  }
}

// Retorna true si el access token expira en menos de UMBRAL_RENOVACION_SEGUNDOS
const UMBRAL_RENOVACION_SEGUNDOS = 10 * 60; // 10 minutos
function tokenProximoAExpirar(token) {
  const payload = decodificarPayloadJwt(token);
  if (!payload || !payload.exp) return false;
  return (payload.exp - Math.floor(Date.now() / 1000)) < UMBRAL_RENOVACION_SEGUNDOS;
}

// Usar VUE_APP_API_URL si existe; en desarrollo fallback directo al backend local
const envBaseURL = process.env.VUE_APP_API_URL;
const isDev = process.env.NODE_ENV === 'development';
const devFallback = 'http://localhost/sicecomv1.0.1/backend_mvc/public/api';
const baseURL = envBaseURL || (isDev ? devFallback : '/api');

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor de request: adjuntar token y hacer refresh proactivo si está por expirar
api.interceptors.request.use(async (config) => {
  const token = obtenerTokenAcceso();

  const url = config.url || '';
  const esEndpointAuth =
    url.startsWith('/autenticacion/cliente/iniciar') ||
    url.startsWith('/autenticacion/cliente/refrescar') ||
    url.startsWith('/autenticacion/cliente/recuperar-clave') ||
    url.startsWith('/clientes/recuperar-clave') ||
    url.startsWith('/clientes/activar-cuenta');

  if (token && !esEndpointAuth && tokenProximoAExpirar(token)) {
    // Refresh proactivo silente: renovar antes de que expire para no interrumpir al usuario
    const refreshToken = obtenerTokenRefresco();
    if (refreshToken && !isRefreshing) {
      isRefreshing = true;
      try {
        const refreshApi = axios.create({
          baseURL: process.env.VUE_APP_API_URL || baseURL,
          headers: { 'Content-Type': 'application/json' }
        });
        const { data } = await refreshApi.post('/autenticacion/cliente/refrescar', { refresh_token: refreshToken });
        const nuevoToken = data?.access_token;
        if (nuevoToken) {
          localStorage.setItem(CLAVE_TOKEN_ACCESO, nuevoToken);
          onRefreshed(nuevoToken);
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${nuevoToken}`;
        }
      } catch {
        // Si falla el refresh proactivo, continuar con el token actual
        // El interceptor de respuesta lo reintentará si llega un 401
      } finally {
        isRefreshing = false;
      }
    }
  }

  if (token && !esEndpointAuth && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

let isRefreshing = false;
let subscribers = [];

function subscribeTokenRefresh(cb) {
  subscribers.push(cb);
}
function onRefreshed(newToken) {
  subscribers.forEach((cb) => cb(newToken));
  subscribers = [];
}

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;

    const originalUrl = original.url || '';
    const esEndpointAuth =
      originalUrl.startsWith('/autenticacion/cliente/iniciar') ||
      originalUrl.startsWith('/autenticacion/cliente/refrescar') ||
      originalUrl.startsWith('/autenticacion/cliente/recuperar-clave') ||
      originalUrl.startsWith('/clientes/recuperar-clave') ||
      originalUrl.startsWith('/clientes/activar-cuenta');

    if (status === 401 && !original._retry) {
      if (esEndpointAuth) {
        return Promise.reject(error);
      }
      const refreshToken = obtenerTokenRefresco();
      if (!refreshToken) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${newToken}`;
            resolve(api.request(original));
          });
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const refreshApi = axios.create({
          baseURL: process.env.VUE_APP_API_URL || baseURL,
          headers: {'Content-Type': 'application/json'}
        });
        const { data } = await refreshApi.post('/autenticacion/cliente/refrescar', { refresh_token: refreshToken });
        const newToken = data?.access_token;
        if (!newToken) throw new Error('No access_token en respuesta de refresco');

        localStorage.setItem(CLAVE_TOKEN_ACCESO, newToken);
        onRefreshed(newToken);

        // Actualizar información del usuario tras refresco
        try {
          const perfilResponse = await refreshApi.get('/autenticacion/cliente/perfil', {
            headers: { 'Authorization': `Bearer ${newToken}` }
          });

          if (perfilResponse.data?.user) {
            localStorage.setItem(CLAVE_USUARIO, JSON.stringify(perfilResponse.data.user));
            import('@/store').then(({ default: store }) => {
              store.commit('setUser', perfilResponse.data.user);
            });
          }

          // Los módulos no se actualizan aquí: datos legacy de /perfil pueden
          // sobrescribir el estado correcto fijado por cargarPermisosEfectivos.

        } catch (perfilError) {
          // perfil no crítico tras refresco
        }

        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      } catch (e) {
        // Solo cerrar sesión si el servidor rechazó explícitamente el refresh token (401/403).
        // Errores de red (sin respuesta) o errores del servidor (5xx) NO deben destruir la sesión
        // porque el usuario puede estar en medio de una factura.
        const codigoRespuesta = e?.response?.status;
        const esRechazoExplicito = codigoRespuesta === 401 || codigoRespuesta === 403;
        if (esRechazoExplicito) {
          localStorage.removeItem(CLAVE_TOKEN_ACCESO);
          localStorage.removeItem(CLAVE_TOKEN_REFRESCO);
          localStorage.removeItem(CLAVE_TOKEN_ACCESO_LEGADO);
          localStorage.removeItem(CLAVE_TOKEN_REFRESCO_LEGADO);
          localStorage.removeItem(CLAVE_USUARIO);
          localStorage.removeItem(CLAVE_USUARIO_LEGADO);
          localStorage.removeItem(CLAVE_MODULOS);
          localStorage.removeItem(CLAVE_MODULOS_LEGADO);
          router.push('/iniciar-sesion');
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
